<template>
  <div 
    ref="viewerContainer" 
    class="pdf-viewer-container" 
    :class="{ 'is-fullscreen': isFullscreen }"
    tabindex="0"
    role="region"
    :aria-label="ariaLabel"
    @keydown="handleKeyDown"
    @wheel="handleWheel"
    @mousedown="startPan"
    @mousemove="doPan"
    @mouseup="endPan"
    @mouseleave="handleMouseLeave"
    @mouseenter="handleMouseEnter"
    @touchstart="handleTouchStart"
    @touchmove.prevent="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <!-- Loading overlay -->
    <div v-if="loading" class="pdf-loading">
      <Loader2 class="spinner" />
      <span>Loading Poster...</span>
    </div>

    <!-- Error message -->
    <div v-if="error" class="pdf-error">
      <p>Unable to load PDF poster.</p>
      <p class="error-detail" v-if="errorMessage">{{ errorMessage }}</p>
      <a :href="pdfUrl" target="_blank" class="download-btn">Open Direct PDF</a>
    </div>

    <!-- Render viewport -->
    <div v-show="!loading && !error" class="pdf-viewport">
      <canvas 
        ref="pdfCanvas" 
        :style="canvasStyle"
        :class="{ 'is-resetting': isResetting }"
        class="pdf-canvas"
      ></canvas>
    </div>

    <!-- Controls overlay -->
    <div v-if="!loading && !error" class="pdf-controls">
      <button class="control-btn" @click="zoomOut" title="Zoom Out" tabindex="-1" aria-label="Zoom Out">
        <ZoomOut :size="20" />
      </button>
      <span class="zoom-indicator">{{ Math.round(scale * 100) }}%</span>
      <button class="control-btn" @click="zoomIn" title="Zoom In" tabindex="-1" aria-label="Zoom In">
        <ZoomIn :size="20" />
      </button>
      <button class="control-btn" @click="resetZoom(true)" title="Reset View" tabindex="-1" aria-label="Reset View">
        <RotateCcw :size="20" />
      </button>
      <div class="control-divider"></div>
      <button class="control-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'" tabindex="-1" :aria-label="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
        <Maximize v-if="!isFullscreen" :size="20" />
        <Minimize v-else :size="20" />
      </button>
      <a :href="pdfUrl" target="_blank" class="control-btn" title="Open PDF in new tab" tabindex="-1" aria-label="Open PDF in new tab">
        <ExternalLink :size="20" />
      </a>
    </div>

    <!-- Tooltip hint -->
    <transition name="fade">
      <div v-if="!loading && !error && showHint" class="viewer-hint">
        Drag to pan • Pinch or Shift + Scroll to zoom
      </div>
    </transition>
  </div>
</template>

<script>
import { ZoomIn, ZoomOut, RotateCcw, Maximize, Minimize, ExternalLink, Loader2 } from 'lucide-vue-next';

export default {
  name: 'PdfViewer',
  components: {
    ZoomIn,
    ZoomOut,
    RotateCcw,
    Maximize,
    Minimize,
    ExternalLink,
    Loader2
  },
  props: {
    pdfUrl: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: 'Robot Design Showcase Poster'
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      showHint: false,
      scale: 1,
      baseScale: 1, // Fit-to-screen scale
      translateX: 0,
      translateY: 0,
      isFullscreen: false,
      isResetting: false,
      
      // Internal PDF.js size references
      pdfWidth: 0,
      pdfHeight: 0,

      // Pan/Zoom state
      isPanning: false,
      startX: 0,
      startY: 0,

      // Touch zoom state
      initialTouchDistance: 0,
      initialTouchScale: 1,
      touchCenterX: 0,
      touchCenterY: 0
    };
  },
  created() {
    // Keep PDF.js class instances non-reactive to avoid Vue 3 Proxy wrapping.
    // Reactive proxies on objects with native ES private fields throw TypeErrors.
    this.pdfDoc = null;
    this.pdfPage = null;
    this.hintTimeout = null;
    this.resetTimeout = null;
  },
  computed: {
    ariaLabel() {
      return `${this.title} interactive viewer. Press Enter or Space for fullscreen, plus or minus to zoom, arrow keys to pan.`;
    },
    canvasStyle() {
      return {
        transform: `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`,
        cursor: this.isPanning ? 'grabbing' : 'grab'
      };
    }
  },
  mounted() {
    this.initPdf();
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    window.addEventListener('resize', this.resetZoom);
  },
  beforeUnmount() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    window.removeEventListener('resize', this.resetZoom);
    clearTimeout(this.hintTimeout);
    clearTimeout(this.resetTimeout);
  },
  methods: {
    async initPdf() {
      try {
        this.loading = true;
        // Dynamically load PDF.js from CDN to keep initial bundle light
        await this.loadPdfJs();

        const pdfjsLib = window.pdfjsLib || window['pdfjs-dist/build/pdf'];
        if (!pdfjsLib) {
          throw new Error('PDF.js library not found on window object.');
        }
        
        // Load document - encodeURI helps resolve filenames with spaces properly
        const encodedUrl = encodeURI(this.pdfUrl);
        const loadingTask = pdfjsLib.getDocument(encodedUrl);
        this.pdfDoc = await loadingTask.promise;
        
        // Get first page (poster is single page)
        this.pdfPage = await this.pdfDoc.getPage(1);
        
        // Render initial canvas
        await this.renderPage();
        this.loading = false;
        
        // Set initial scale to fit container
        this.$nextTick(() => {
          this.resetZoom();
          this.showTemporaryHint(4000);
        });
      } catch (err) {
        console.error('Error loading PDF:', err);
        this.error = true;
        this.errorMessage = err.message || err.toString();
        this.loading = false;
      }
    },

    loadPdfJs() {
      return new Promise((resolve, reject) => {
        const existingLib = window.pdfjsLib || window['pdfjs-dist/build/pdf'];
        if (existingLib) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          const pdfjsLib = window.pdfjsLib || window['pdfjs-dist/build/pdf'];
          if (pdfjsLib) {
            try {
              // Bypass Same-Origin policy for Web Worker by using a Blob URL
              const workerUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
              const blobCode = `importScripts('${workerUrl}');`;
              const blob = new Blob([blobCode], { type: 'application/javascript' });
              pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
            } catch (workerErr) {
              console.warn('Failed to configure PDF.js worker with Blob URL, falling back:', workerErr);
              pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            }
            resolve();
          } else {
            reject(new Error('PDF.js loaded but global object not found'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load PDF.js'));
        document.head.appendChild(script);
      });
    },

    async renderPage() {
      if (!this.pdfPage) return;

      // Render at a high base resolution (e.g. scale 2.0) so it looks sharp when zoomed in
      const renderScale = 2.0;
      const viewport = this.pdfPage.getViewport({ scale: renderScale });
      
      const canvas = this.$refs.pdfCanvas;
      if (!canvas) return;
      
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      this.pdfWidth = viewport.width;
      this.pdfHeight = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await this.pdfPage.render(renderContext).promise;
    },

    resetZoom(animate = false) {
      const container = this.$refs.viewerContainer;
      if (!container || !this.pdfWidth) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate scale to fit completely inside container
      const scaleX = containerWidth / this.pdfWidth;
      const scaleY = containerHeight / this.pdfHeight;
      
      // Fit to container (no padding for borderless rendering)
      const padding = 1.0;
      this.baseScale = Math.min(scaleX, scaleY) * padding;
      this.scale = this.baseScale;

      // Explicitly center the canvas translations
      this.translateX = (containerWidth - this.pdfWidth * this.scale) / 2;
      this.translateY = (containerHeight - this.pdfHeight * this.scale) / 2;

      if (animate) {
        this.isResetting = true;
        setTimeout(() => {
          this.isResetting = false;
        }, 300);
      }
    },

    showTemporaryHint(duration = 3000) {
      this.showHint = true;
      clearTimeout(this.hintTimeout);
      this.hintTimeout = setTimeout(() => {
        this.showHint = false;
      }, duration);
    },

    clampTranslation() {
      const container = this.$refs.viewerContainer;
      if (!container || !this.pdfWidth) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const contentWidth = this.pdfWidth * this.scale;
      const contentHeight = this.pdfHeight * this.scale;

      // Soft boundaries: allow dragging past container edges so users can center text at the corners.
      // We only clamp so the poster cannot be dragged entirely off-screen (keep at least 150px visible).
      const minVisibleSize = 150;
      
      const minX = -contentWidth + minVisibleSize;
      const maxX = containerWidth - minVisibleSize;
      const minY = -contentHeight + minVisibleSize;
      const maxY = containerHeight - minVisibleSize;

      this.translateX = Math.min(maxX, Math.max(minX, this.translateX));
      this.translateY = Math.min(maxY, Math.max(minY, this.translateY));
    },

    zoomIn() {
      this.adjustZoom(1.2);
    },

    zoomOut() {
      this.adjustZoom(0.8);
    },

    adjustZoom(factor) {
      const container = this.$refs.viewerContainer;
      if (!container) return;
      
      const centerX = container.clientWidth / 2;
      const centerY = container.clientHeight / 2;
      
      this.zoomAtPoint(centerX, centerY, factor);
    },

    zoomAtPoint(clientX, clientY, factor) {
      const container = this.$refs.viewerContainer;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;

      // Limit zoom-out to baseScale so it doesn't get tiny
      const newScale = Math.max(this.baseScale, Math.min(this.baseScale * 12, this.scale * factor));
      if (newScale === this.scale) return;

      // Calculate new translations to keep target point under the pointer/center
      const ratio = newScale / this.scale;
      this.translateX = clickX - (clickX - this.translateX) * ratio;
      this.translateY = clickY - (clickY - this.translateY) * ratio;
      this.scale = newScale;

      this.clampTranslation();
    },

    handleWheel(event) {
      if (event.shiftKey) {
        event.preventDefault();
        const zoomFactor = event.deltaY < 0 ? 1.1 : 0.9;
        this.zoomAtPoint(event.clientX, event.clientY, zoomFactor);
      } else {
        // Remind the user to hold Shift to zoom
        this.showTemporaryHint(2000);
      }
    },

    // Mouse Pan logic
    startPan(event) {
      if (this.loading || this.error) return;
      this.isPanning = true;
      this.startX = event.clientX - this.translateX;
      this.startY = event.clientY - this.translateY;
    },

    doPan(event) {
      if (!this.isPanning) return;
      this.translateX = event.clientX - this.startX;
      this.translateY = event.clientY - this.startY;
      
      this.clampTranslation();
    },

    endPan() {
      this.isPanning = false;
    },



    handleMouseEnter() {
      // Cancel any pending reset zoom timer if the mouse comes back
      clearTimeout(this.resetTimeout);
      this.showTemporaryHint(3000);
    },

    handleMouseLeave() {
      this.endPan();
      
      // Hide the tooltip on mouse leave
      this.showHint = false;
      clearTimeout(this.hintTimeout);

      // Start 3-second buffer before resetting zoom
      clearTimeout(this.resetTimeout);
      this.resetTimeout = setTimeout(() => {
        this.resetZoom(true);
      }, 3000);
    },

    // Touch support (Pinch to Zoom and Pan)
    handleTouchStart(event) {
      if (this.loading || this.error) return;

      if (event.touches.length === 1) {
        // Single touch -> Pan
        this.isPanning = true;
        this.startX = event.touches[0].clientX - this.translateX;
        this.startY = event.touches[0].clientY - this.translateY;
      } else if (event.touches.length === 2) {
        // Multi touch -> Zoom
        this.isPanning = false;
        
        const t1 = event.touches[0];
        const t2 = event.touches[1];
        
        this.initialTouchDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        this.initialTouchScale = this.scale;
        
        // Touch center point
        this.touchCenterX = (t1.clientX + t2.clientX) / 2;
        this.touchCenterY = (t1.clientY + t2.clientY) / 2;
      }
    },

    handleTouchMove(event) {
      if (event.touches.length === 1 && this.isPanning) {
        this.translateX = event.touches[0].clientX - this.startX;
        this.translateY = event.touches[0].clientY - this.startY;
        this.clampTranslation();
      } else if (event.touches.length === 2) {
        const t1 = event.touches[0];
        const t2 = event.touches[1];
        
        const currentDistance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        if (this.initialTouchDistance > 0) {
          const factor = currentDistance / this.initialTouchDistance;
          const targetScale = Math.max(this.baseScale, Math.min(this.baseScale * 12, this.initialTouchScale * factor));
          const ratio = targetScale / this.scale;
          
          const container = this.$refs.viewerContainer;
          if (container) {
            const rect = container.getBoundingClientRect();
            const clickX = this.touchCenterX - rect.left;
            const clickY = this.touchCenterY - rect.top;
            
            this.translateX = clickX - (clickX - this.translateX) * ratio;
            this.translateY = clickY - (clickY - this.translateY) * ratio;
            this.scale = targetScale;

            this.clampTranslation();
          }
        }
      }
    },

    handleTouchEnd() {
      this.isPanning = false;
      this.initialTouchDistance = 0;
    },

    toggleFullscreen() {
      const container = this.$refs.viewerContainer;
      if (!container) return;

      if (!document.fullscreenElement) {
        container.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable full-screen mode: ${err.message}`);
        });
      } else {
        document.exitFullscreen();
      }
    },

    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
      // Wait for layout to settle, then reset/center zoom
      setTimeout(() => {
        this.resetZoom();
      }, 150);
    },

    handleKeyDown(event) {
      switch (event.key) {
        case 'Enter':
        case ' ':
        case 'f':
        case 'F':
          event.preventDefault();
          this.toggleFullscreen();
          break;
        case '+':
        case '=':
          event.preventDefault();
          this.zoomIn();
          break;
        case '-':
        case '_':
          event.preventDefault();
          this.zoomOut();
          break;
        case '0':
        case 'r':
        case 'R':
          event.preventDefault();
          this.resetZoom(true);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.translateY += 40;
          this.clampTranslation();
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.translateY -= 40;
          this.clampTranslation();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.translateX += 40;
          this.clampTranslation();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.translateX -= 40;
          this.clampTranslation();
          break;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pdf-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: none;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  outline: none;

  &:focus-visible {
    outline: 2px dashed var(--regular, #02979d);
    outline-offset: 4px;
    border-radius: var(--border-radius-md, 8px);
  }

  &.is-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    margin-left: 0;
    background-color: #0b0f19;
    border-radius: 0;
    border: none;
    z-index: 999999;
  }
}

.pdf-viewport {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.pdf-canvas {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
  transition: transform 0.05s ease-out;

  &.is-resetting {
    transition: transform 0.3s ease-in-out;
  }
}

.pdf-loading, .pdf-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #a0aec0;
  font-family: inherit;

  .spinner {
    animation: spin 1.5s linear infinite;
    color: #4299e1;
    width: 32px;
    height: 32px;
  }
}

.pdf-error {
  color: #fc8181;
  text-align: center;
  padding: 20px;

  .download-btn {
    margin-top: 8px;
    padding: 8px 16px;
    background-color: #e53e3e;
    color: white;
    border-radius: 6px;
    text-decoration: none;
    font-size: 0.9em;
    font-weight: 600;
    transition: background-color 0.2s;

    &:hover {
      background-color: #c53030;
    }
  }

  .error-detail {
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 8px 12px;
    border-radius: 6px;
    max-width: 80%;
    margin: 8px auto;
    font-family: monospace;
    word-break: break-all;
  }
}

.pdf-controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 30px;
  z-index: 10;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);

  .control-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.85);
    padding: 8px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
    }

    &:active {
      transform: scale(0.92);
    }
  }

  .zoom-indicator {
    color: #fff;
    font-size: 0.85em;
    font-weight: 600;
    min-width: 48px;
    text-align: center;
  }

  .control-divider {
    width: 1px;
    height: 20px;
    background: rgba(255, 255, 255, 0.2);
    margin: 0 4px;
  }
}

.viewer-hint {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.75);
  color: rgba(255, 255, 255, 0.95);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8em;
  font-weight: var(--font-weight);
  pointer-events: none;
  z-index: 100;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Vue Fade Transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}
.fade-enter-to, .fade-leave-from {
  opacity: 1;
  transform: translate(-50%, 0);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
